import {
  getBorderWoodPattern,
  getWoodPattern,
  roundedRect,
} from '@bn-games/render-helpers';
import type { Chain } from '../game/types.js';
import {
  BOARD_BORDER_THICKNESS,
  CHAIN_REGION_INSET,
  TILE_CORNER_RADIUS,
} from '../utils/constants.js';
import { layoutChain, type ChainLayout, type TurnsByArm } from './geometry.js';
import type { RenderPalette } from './palette.js';
import { drawTileFace, rectBounds, type TileRect } from './TileRenderer.js';

/**
 * Region of the canvas reserved for the chain. The hand zones sit outside
 * this rect. The chain centers on `(x + w/2, y + h/2)` and extends outward
 * from the spinner.
 */
export interface ChainRegion {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export function drawTable(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  palette: RenderPalette,
): void {
  drawBorderFrame(ctx, canvasWidth, canvasHeight, palette);
  drawPlaySurface(ctx, canvasWidth, canvasHeight, palette);
  drawCornerOrnaments(ctx, canvasWidth, canvasHeight, palette);
}

/**
 * Paints a sunken-cavity bevel around the chain region: dark band on the
 * top + left interior edge (light source from upper-left casts shadow into
 * the recess), subtle highlight on the bottom + right. Sits on top of the
 * board-wood play surface drawn by `drawTable`, before tiles are rendered.
 * The trays sit on the surrounding shelf — together with this recess, the
 * chain region reads as physically lower than the player hands.
 */
export function drawChainRegionRecess(
  ctx: CanvasRenderingContext2D,
  region: ChainRegion,
  _palette: RenderPalette,
): void {
  const { x, y, width: w, height: h } = region;
  ctx.save();

  // Slightly darken the floor so the recess reads as a separate, lower
  // plane from the shelf wood. Kept subtle so tiles + drop outlines stay
  // legible.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
  ctx.fillRect(x, y, w, h);

  // Deep shadow on top + left interior — the lip casts onto the floor.
  const shadowGradTop = ctx.createLinearGradient(x, y, x, y + 10);
  shadowGradTop.addColorStop(0, 'rgba(0, 0, 0, 0.55)');
  shadowGradTop.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = shadowGradTop;
  ctx.fillRect(x, y, w, 10);

  const shadowGradLeft = ctx.createLinearGradient(x, y, x + 10, y);
  shadowGradLeft.addColorStop(0, 'rgba(0, 0, 0, 0.55)');
  shadowGradLeft.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = shadowGradLeft;
  ctx.fillRect(x, y, 10, h);

  // Subtle highlight on bottom + right — light reflects off the lip into
  // the recess floor.
  const highlightGradBottom = ctx.createLinearGradient(x, y + h - 6, x, y + h);
  highlightGradBottom.addColorStop(0, 'rgba(255, 220, 160, 0)');
  highlightGradBottom.addColorStop(1, 'rgba(255, 220, 160, 0.14)');
  ctx.fillStyle = highlightGradBottom;
  ctx.fillRect(x, y + h - 6, w, 6);

  const highlightGradRight = ctx.createLinearGradient(x + w - 6, y, x + w, y);
  highlightGradRight.addColorStop(0, 'rgba(255, 220, 160, 0)');
  highlightGradRight.addColorStop(1, 'rgba(255, 220, 160, 0.14)');
  ctx.fillStyle = highlightGradRight;
  ctx.fillRect(x + w - 6, y, 6, h);

  // Hairline outlining the recess opening — top/left dark, bottom/right
  // light — sells the lip edge against the surrounding shelf wood.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.fillRect(x, y, w, 1);
  ctx.fillRect(x, y, 1, h);
  ctx.fillStyle = 'rgba(255, 220, 160, 0.18)';
  ctx.fillRect(x, y + h - 1, w, 1);
  ctx.fillRect(x + w - 1, y, 1, h);

  ctx.restore();
}

function drawBorderFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  palette: RenderPalette,
): void {
  ctx.save();
  const dims = { width: w, height: h };

  // Border wood fills the whole canvas; play surface is punched in later.
  ctx.fillStyle = getBorderWoodPattern(ctx, palette.wood, dims);
  ctx.fillRect(0, 0, w, h);

  // Outer bevel — light catches top/left, shadow on bottom/right.
  ctx.fillStyle = 'rgba(255, 220, 160, 0.22)';
  ctx.fillRect(0, 0, w, 2);
  ctx.fillRect(0, 0, 2, h);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.30)';
  ctx.fillRect(0, h - 2, w, 2);
  ctx.fillRect(w - 2, 0, 2, h);

  // Inner bevel around the inset playing surface — subtle dip into the wood.
  const ix = BOARD_BORDER_THICKNESS;
  const iy = BOARD_BORDER_THICKNESS;
  const iw = w - 2 * BOARD_BORDER_THICKNESS;
  const ih = h - 2 * BOARD_BORDER_THICKNESS;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.30)';
  ctx.fillRect(ix - 1, iy - 1, iw + 2, 1);
  ctx.fillRect(ix - 1, iy - 1, 1, ih + 2);
  ctx.fillStyle = 'rgba(255, 220, 160, 0.12)';
  ctx.fillRect(ix - 1, iy + ih, iw + 2, 1);
  ctx.fillRect(ix + iw, iy - 1, 1, ih + 2);

  ctx.restore();
}

function drawPlaySurface(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  palette: RenderPalette,
): void {
  const x = BOARD_BORDER_THICKNESS;
  const y = BOARD_BORDER_THICKNESS;
  const iw = w - 2 * BOARD_BORDER_THICKNESS;
  const ih = h - 2 * BOARD_BORDER_THICKNESS;

  ctx.save();
  ctx.fillStyle = getWoodPattern(ctx, palette.wood, { width: w, height: h });
  ctx.fillRect(x, y, iw, ih);

  // Hairline inner-edge shadow so the wood plate reads as recessed into the
  // frame, not glued onto it.
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.18)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, iw - 1, ih - 1);
  ctx.restore();
}

function drawCornerOrnaments(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  palette: RenderPalette,
): void {
  const inset = Math.round(BOARD_BORDER_THICKNESS / 2);
  const positions = [
    { x: inset, y: inset },
    { x: w - inset, y: inset },
    { x: inset, y: h - inset },
    { x: w - inset, y: h - inset },
  ];
  for (const p of positions) drawBrassDiamond(ctx, p.x, p.y, palette);
}

function drawBrassDiamond(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: RenderPalette,
): void {
  const r = 5;
  ctx.save();

  // Recessed seat.
  ctx.beginPath();
  ctx.moveTo(cx, cy - r - 1);
  ctx.lineTo(cx + r + 1, cy);
  ctx.lineTo(cx, cy + r + 1);
  ctx.lineTo(cx - r - 1, cy);
  ctx.closePath();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fill();

  // Brass face.
  const grad = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  grad.addColorStop(0, palette.brassHi);
  grad.addColorStop(0.5, palette.brass);
  grad.addColorStop(1, palette.brassDeep);
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r, cy);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r, cy);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, 0.8, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(40, 25, 8, 0.9)';
  ctx.fill();

  ctx.restore();
}

export function drawChainRegionBorder(
  ctx: CanvasRenderingContext2D,
  region: ChainRegion,
  palette: RenderPalette,
): void {
  ctx.save();
  ctx.strokeStyle = palette.handZoneBorder;
  ctx.lineWidth = 1;
  ctx.setLineDash([6, 6]);
  ctx.strokeRect(
    region.x + CHAIN_REGION_INSET,
    region.y + CHAIN_REGION_INSET,
    region.width - CHAIN_REGION_INSET * 2,
    region.height - CHAIN_REGION_INSET * 2,
  );
  ctx.restore();
}

export function computeChainLayout(
  chain: Chain,
  region: ChainRegion,
  turnsByArm: TurnsByArm = [],
): ChainLayout {
  const cx = region.x + region.width / 2;
  const cy = region.y + region.height / 2;
  return layoutChain(chain, cx, cy, turnsByArm);
}

/**
 * Draw every tile of the chain (face up). Caller is responsible for
 * computing the layout first (so input code and renderer share the same
 * placement) and passing the result here.
 */
export function drawChain(
  ctx: CanvasRenderingContext2D,
  layout: ChainLayout,
  palette: RenderPalette,
): void {
  for (const placed of layout.tiles) {
    drawTileFace(ctx, placed.rect, placed.halfA, placed.halfB, palette);
  }
}

/**
 * Tile-shaped dashed outline showing where the dragged piece will land. Used
 * both for legal-but-unsnapped arms (faint variant) and for the current
 * snap target (bright variant with a soft glow). The caller computes the
 * exact tile slot via `nextTileSlot` / `leadTileSlot` from `geometry.ts`
 * so the outline matches the orientation a real tile would take.
 */
export function drawDropSlotOutline(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  palette: RenderPalette,
  options: { snap?: boolean } = {},
): void {
  const { x, y, width, height } = rectBounds(rect);
  ctx.save();
  if (options.snap) {
    ctx.shadowColor = palette.validArmGlow;
    ctx.shadowBlur = 10;
    ctx.strokeStyle = palette.selectedRing;
    ctx.lineWidth = 2.5;
    ctx.setLineDash([4, 3]);
  } else {
    ctx.strokeStyle = palette.validArmGlow;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 5]);
  }
  roundedRect(ctx, x + 0.5, y + 0.5, width - 1, height - 1, TILE_CORNER_RADIUS);
  ctx.stroke();
  ctx.restore();
}
